import React from 'react'
import styled from 'styled-components'
import { useTranslation } from 'react-i18next'

// Components
import LayoverModal from './feedback/StickyHeaderLayoverModal'
import Text, { MarkDownText } from './Text'
import ResultsHeader from './ResultsHeader'
import { SelectContact } from './onboarding/atoms/SelectContact'
import { RowAligned } from './layout/LayoutAtoms'
import Button from './Button'

// Utils
import { useAppState, useAppDispatch } from '../context/AppContext'
import { formatDate } from '../utils/dateHelper'
import { DISABLE_USER, DELETE_ALL_MEMBER_DATA } from '../constants/api'

// Types
import { ICompanyMember } from '../common/models/Company'

interface IProps {
  closeModal: () => void
  memberIdsToBeDeleted: string[]
}

const ArchivedUsersModal: React.FC<IProps> = ({
  closeModal,
  memberIdsToBeDeleted,
}) => {
  const { t } = useTranslation()
  const appDispatch = useAppDispatch()
  const { company, userAuthToken } = useAppState()

  const archivedMembers = company?.allMembers.filter(
    (m) => m.isDisabled && !m.isDeleted
  )
  const isMemberInDeleteQueue = (memberId: string) => {
    return memberIdsToBeDeleted.includes(memberId)
  }

  const removeFnc = async (member: ICompanyMember) => {
    appDispatch({
      type: 'updateAlertPopup',
      payload: {
        title: t('requests.removeTitle', { name: member.name }),
        subtitle: t('general.areYouSure'),
        content: (
          <MarkDownText textAlign="center">
            {t('requests.removeText')}
          </MarkDownText>
        ),
        actionButton: t('general.actions.removePermanently'),
        dismissButton: t('general.actions.cancel'),
        onSubmit: async () => {
          const deleteReq = await fetch(DELETE_ALL_MEMBER_DATA(member._id), {
            method: 'POST',
            headers: {
              Authorization: 'Bearer ' + (userAuthToken || ''),
            },
          })
          if (deleteReq.ok) {
            appDispatch({
              type: 'updateNotification',
              payload: {
                show: true,
                label: t(`requests.deletedShortly`),
              },
            })
            appDispatch({
              type: 'updateShouldLoadCompany',
              payload: true,
            })
            appDispatch({
              type: 'updateShouldLoadRequests',
              payload: true,
            })
          } else {
            appDispatch({
              type: 'updateNotification',
              payload: {
                show: true,
                label: t('requests.removeError'),
              },
            })
          }
          appDispatch({
            type: 'updateAlertPopup',
            payload: undefined,
          })
        },
      },
    })
  }

  const unarchiveFnc = async (member: ICompanyMember) => {
    const submitDisableRequest = await fetch(DISABLE_USER(member._id), {
      method: 'POST',
      headers: {
        Authorization: 'Bearer ' + (userAuthToken || ''),
      },
    })

    if (submitDisableRequest.ok) {
      appDispatch({
        type: 'updateNotification',
        payload: {
          show: true,
          label: t(`profileSettings.unarchived`, { name: member.name }),
        },
      })
      appDispatch({
        type: 'updateShouldLoadCompany',
        payload: true,
      })
      appDispatch({
        type: 'updateShouldLoadRequests',
        payload: true,
      })
    } else {
      appDispatch({
        type: 'updateNotification',
        payload: {
          show: true,
          label: t('profileSettings.errorEditProfile'),
        },
      })
    }
  }

  return archivedMembers ? (
    <LayoverModal
      headerConfig={{
        title: t('requests.deleteArchived'),
        subtitle: t('general.openRequest'),
      }}
      closeModal={closeModal}
      children={
        <>
          <Text textStyle="bodySmall" marginBottom={12}>
            {t('requests.deleteArchivedText')}
          </Text>
          <ResultsHeader
            title={t('general.users.archivedUsers')}
            resultsLength={archivedMembers.length}
            rightLabel={t('general.actions.actions')}
          />
          {archivedMembers?.map((member) => (
            <SelectContact
              key={`selectedMember-${member._id}`}
              contactMember={member}
              title={member.name}
              subtitle={
                member.archivedDate &&
                t('requests.archivedOn', {
                  date: formatDate(new Date(member.archivedDate || '')),
                })
              }
              disabled
              actionElement={
                <RowAligned margin="0 0 0 auto">
                  {!isMemberInDeleteQueue(member._id) ? (
                    <>
                      <RemoveButton
                        caption={t('general.actions.remove')}
                        color="secondaryLight"
                        textColor="secondary"
                        hoverColor="secondary"
                        icon="TrashCan"
                        onClick={() => removeFnc(member)}
                      />
                      <Button
                        caption={t('general.actions.unarchive')}
                        colorVariant="secondary"
                        icon="Archive"
                        onClick={() => unarchiveFnc(member)}
                      />
                    </>
                  ) : (
                    <Text textStyle="caption" color="text72">
                      {t('requests.deletedShortly')}
                    </Text>
                  )}
                </RowAligned>
              }
            />
          ))}
        </>
      }
    />
  ) : null
}

export default ArchivedUsersModal

const RemoveButton = styled(Button)`
  margin-right: 12px;
`
